
import { useEffect, useState, useCallback, useMemo, useRef, memo } from 'react';
import {
    Box,
    Flex,
    Grid,
    Heading,
    Text,
    Button,
    Input,
    IconButton,
    Badge,
    Skeleton,
    SimpleGrid,
    Link as ChakraLink,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import io from 'socket.io-client';
import { apiService, BASE_URL } from 'services/service';
import useDebounce from 'hooks/useDebounce';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS — matches the rest of the app
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SUCCESS     = '#10B981';
const SUCCESS_BG  = '#ECFDF5';
const DANGER      = '#E53E3E';
const DANGER_BG   = '#FEF2F2';
const WARNING     = '#EA580C';
const WARNING_BG  = '#FFF7ED';
const INFO        = '#2563EB';
const INFO_BG     = '#EFF6FF';

const FONT = "'Bricolage Grotesque', sans-serif";

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

const ITEMS_PER_PAGE      = 10;
const SEARCH_DEBOUNCE_MS  = 700;

const SOCKET_URL = BASE_URL;

/* Dev-only logger — silent in production builds */
const dlog = (...args) => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

/* ─────────────────────────────────────────────────────────────
   STATUS METADATA
   ───────────────────────────────────────────────────────────── */

const STATUS_META = {
    pending:           { label: 'Pending',          color: MUTED,   bg: SURFACE_ALT },
    created:           { label: 'Created',          color: INFO,    bg: INFO_BG     },
    in_transit:        { label: 'In Transit',       color: WARNING, bg: WARNING_BG  },
    out_for_delivery:  { label: 'Out for Delivery', color: INFO,    bg: INFO_BG     },
    delivered:         { label: 'Delivered',        color: SUCCESS, bg: SUCCESS_BG  },
    cancelled:         { label: 'Cancelled',        color: DANGER,  bg: DANGER_BG   },
    exception:         { label: 'Exception',        color: DANGER,  bg: DANGER_BG   },
};

const STATUS_OPTIONS = Object.keys(STATUS_META);

const getStatusMeta = (status) =>
    STATUS_META[status] || { label: status || 'Unknown', color: MUTED, bg: SURFACE_ALT };

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────── */

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        return new Date(dateString).toLocaleString('en-NG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

/* Strip empty/null filter values before sending to API */
const cleanFilters = (filters) => {
    const out = {};
    Object.entries(filters || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') out[k] = v;
    });
    return out;
};

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */

const RefreshIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0114.85-3.36L23 10M1 14l4.65 4.36A9 9 0 0020.5 15"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

// const SearchIcon = (props) => (
//     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
//         <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
//         <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//     </svg>
// );

const ChevronLeftIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronDownIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronRightIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ExternalIcon = (props) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M14 4h6v6M20 4l-9 9M9 5H5a1 1 0 00-1 1v13a1 1 0 001 1h13a1 1 0 001-1v-4"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InboxIcon = (props) => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 5h13l3 7v6a2 2 0 01-2 2h-15a2 2 0 01-2-2v-6l3-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   STATUS BADGE
   ───────────────────────────────────────────────────────────── */

const StatusBadge = memo(function StatusBadge({ status }) {
    const meta = getStatusMeta(status);
    return (
        <Badge
            bg={meta.bg}
            color={meta.color}
            fontSize="10px"
            fontWeight="800"
            px="8px"
            py="2px"
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="0.04em"
            border="1px solid"
            borderColor={`${meta.color}30`}
            whiteSpace="nowrap"
        >
            {meta.label}
        </Badge>
    );
});

/* ─────────────────────────────────────────────────────────────
   STATS CARD
   ───────────────────────────────────────────────────────────── */

const StatCard = memo(function StatCard({ label, value, accent = NAVY }) {
    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            p="14px 16px"
            boxShadow={SHADOW_SM}
            position="relative"
            overflow="hidden"
        >
            {/* Accent strip on left */}
            <Box position="absolute" left="0" top="0" bottom="0" w="3px" bg={accent} />
            <Text
                fontSize="10px"
                fontWeight="800"
                color={FAINT}
                letterSpacing="0.08em"
                textTransform="uppercase"
                mb="6px"
                fontFamily={FONT}
            >
                {label}
            </Text>
            <Heading
                as="p"
                fontSize="22px"
                fontWeight="800"
                color={INK}
                fontFamily={FONT}
                lineHeight="1"
            >
                {Number(value || 0).toLocaleString('en-NG')}
            </Heading>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   CONNECTION STATUS INDICATOR
   ───────────────────────────────────────────────────────────── */

const ConnectionDot = memo(function ConnectionDot({ connected }) {
    return (
        <Flex align="center" gap="6px">
            <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={connected ? SUCCESS : FAINT}
                boxShadow={connected ? `0 0 0 3px ${SUCCESS}22` : 'none'}
                transition="all 0.2s ease"
            />
            <Text fontSize="11px" color={MUTED} fontWeight="600">
                {connected ? 'Live updates active' : 'Reconnecting…'}
            </Text>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   FILTER BAR
   ───────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────
   STATUS FILTER DROPDOWN — refined replacement for native <select>
   ───────────────────────────────────────────────────────────── */

const StatusFilterDropdown = memo(function StatusFilterDropdown({ value, onChange }) {
    const selectedMeta = value ? getStatusMeta(value) : null;

    return (
        <Menu placement="bottom-start" matchWidth autoSelect={false}>
            {({ isOpen }) => (
                <>
                    <MenuButton
                        as={Button}
                        rightIcon={
                            <Box
                                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                transition="transform 0.18s ease"
                                color={FAINT}
                            >
                                <ChevronDownIcon />
                            </Box>
                        }
                        w="full"
                        h="36px"
                        px="12px"
                        bg={SURFACE_ALT}
                        border="1px solid"
                        borderColor={isOpen ? NAVY : BORDER}
                        boxShadow={isOpen ? `0 0 0 3px ${CYAN}` : 'none'}
                        borderRadius="8px"
                        textAlign="left"
                        fontWeight="500"
                        fontFamily={FONT}
                        _hover={{ borderColor: isOpen ? NAVY : '#CBD5E0', bg: SURFACE_ALT }}
                        _active={{ bg: SURFACE_ALT }}
                        sx={{
                            '& > span': {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flex: 1,
                                minWidth: 0,
                            },
                        }}
                    >
                        {selectedMeta ? (
                            <Flex align="center" gap="8px" minW="0">
                                <Box
                                    w="8px"
                                    h="8px"
                                    borderRadius="full"
                                    bg={selectedMeta.color}
                                    flexShrink={0}
                                />
                                <Text
                                    fontSize="12px"
                                    color={INK}
                                    fontWeight="600"
                                    noOfLines={1}
                                >
                                    {selectedMeta.label}
                                </Text>
                            </Flex>
                        ) : (
                            <Text fontSize="12px" color={FAINT} fontWeight="500">
                                All statuses
                            </Text>
                        )}
                    </MenuButton>

                    <MenuList
                        p="6px"
                        borderRadius="10px"
                        border="1px solid"
                        borderColor={BORDER}
                        boxShadow={SHADOW_SM}
                        bg={SURFACE}
                        fontFamily={FONT}
                        minW="0"
                        zIndex={20}
                    >
                        {/* "All statuses" option */}
                        <MenuItem
                            onClick={() => onChange('')}
                            bg={!value ? CYAN : 'transparent'}
                            color={!value ? NAVY : MUTED}
                            fontSize="12px"
                            fontWeight={!value ? '700' : '500'}
                            borderRadius="6px"
                            mb="2px"
                            px="10px"
                            py="8px"
                            _hover={{ bg: !value ? CYAN : SURFACE_ALT, color: NAVY }}
                            _focus={{ bg: !value ? CYAN : SURFACE_ALT, color: NAVY }}
                        >
                            <Flex align="center" justify="space-between" w="full" gap="8px">
                                <Flex align="center" gap="8px">
                                    <Box w="8px" h="8px" borderRadius="full" bg={FAINT} />
                                    <Text as="span">All statuses</Text>
                                </Flex>
                                {!value && <Box color={NAVY}><CheckIcon /></Box>}
                            </Flex>
                        </MenuItem>

                        {/* Status options */}
                        {STATUS_OPTIONS.map((s) => {
                            const meta = STATUS_META[s];
                            const isSelected = value === s;
                            return (
                                <MenuItem
                                    key={s}
                                    onClick={() => onChange(s)}
                                    bg={isSelected ? CYAN : 'transparent'}
                                    color={isSelected ? NAVY : MUTED}
                                    fontSize="12px"
                                    fontWeight={isSelected ? '700' : '500'}
                                    borderRadius="6px"
                                    mb="2px"
                                    px="10px"
                                    py="8px"
                                    _hover={{ bg: isSelected ? CYAN : SURFACE_ALT, color: NAVY }}
                                    _focus={{ bg: isSelected ? CYAN : SURFACE_ALT, color: NAVY }}
                                >
                                    <Flex align="center" justify="space-between" w="full" gap="8px">
                                        <Flex align="center" gap="8px" minW="0">
                                            <Box
                                                w="8px"
                                                h="8px"
                                                borderRadius="full"
                                                bg={meta.color}
                                                flexShrink={0}
                                            />
                                            <Text as="span" noOfLines={1}>
                                                {meta.label}
                                            </Text>
                                        </Flex>
                                        {isSelected && <Box color={NAVY}><CheckIcon /></Box>}
                                    </Flex>
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </>
            )}
        </Menu>
    );
});

/* ─────────────────────────────────────────────────────────────
   FILTER BAR
   ───────────────────────────────────────────────────────────── */

const FilterBar = memo(function FilterBar({
    filters, onFilterChange, onClearAll, hasActiveFilters,
}) {
    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            p="14px"
            mb="16px"
            boxShadow={SHADOW_SM}
        >
            <Flex justify="space-between" align="center" mb="12px">
                <Text fontSize="12px" fontWeight="800" color={INK} fontFamily={FONT}>
                    Filters
                </Text>
                {hasActiveFilters && (
                    <Button
                        size="xs"
                        variant="ghost"
                        color={MUTED}
                        fontSize="11px"
                        fontWeight="700"
                        onClick={onClearAll}
                        _hover={{ color: NAVY, bg: 'transparent', textDecoration: 'underline' }}
                        h="22px"
                        px="4px"
                    >
                        Clear all
                    </Button>
                )}
            </Flex>

            <Grid
                templateColumns={{
                    base: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(5, 1fr)',
                }}
                gap="10px"
            >
                {/* Status */}
                <Box>
                    <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                        STATUS
                    </Text>
                    <StatusFilterDropdown
                        value={filters.status}
                        onChange={(v) => onFilterChange('status', v)}
                    />
                </Box>

                {/* Shipment ID */}
                <Box>
                    <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                        SHIPMENT ID
                    </Text>
                    <Input
                        value={filters.shipment_id}
                        onChange={(e) => onFilterChange('shipment_id', e.target.value)}
                        placeholder="Search shipment ID"
                        size="sm"
                        h="36px"
                        fontSize="12px"
                        borderColor={BORDER}
                        bg={SURFACE_ALT}
                        fontFamily={FONT}
                        _hover={{ borderColor: '#CBD5E0' }}
                        _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                        borderRadius="8px"
                        _placeholder={{ color: FAINT }}
                    />
                </Box>

                {/* Order ID */}
                <Box>
                    <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                        ORDER ID
                    </Text>
                    <Input
                        value={filters.order_id}
                        onChange={(e) => onFilterChange('order_id', e.target.value)}
                        placeholder="Search order ID"
                        size="sm"
                        h="36px"
                        fontSize="12px"
                        borderColor={BORDER}
                        bg={SURFACE_ALT}
                        fontFamily={FONT}
                        _hover={{ borderColor: '#CBD5E0' }}
                        _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                        borderRadius="8px"
                        _placeholder={{ color: FAINT }}
                    />
                </Box>

                {/* Start Date */}
                <Box>
                    <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                        FROM
                    </Text>
                    <Input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => onFilterChange('start_date', e.target.value)}
                        size="sm"
                        h="36px"
                        fontSize="12px"
                        borderColor={BORDER}
                        bg={SURFACE_ALT}
                        fontFamily={FONT}
                        _hover={{ borderColor: '#CBD5E0' }}
                        _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                        borderRadius="8px"
                    />
                </Box>

                {/* End Date */}
                <Box>
                    <Text fontSize="10px" fontWeight="700" color={MUTED} mb="4px" letterSpacing="0.04em">
                        TO
                    </Text>
                    <Input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => onFilterChange('end_date', e.target.value)}
                        size="sm"
                        h="36px"
                        fontSize="12px"
                        borderColor={BORDER}
                        bg={SURFACE_ALT}
                        fontFamily={FONT}
                        _hover={{ borderColor: '#CBD5E0' }}
                        _focus={{ borderColor: NAVY, boxShadow: `0 0 0 3px ${CYAN}`, bg: SURFACE }}
                        borderRadius="8px"
                    />
                </Box>
            </Grid>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   TABLE ROW (desktop)
   ───────────────────────────────────────────────────────────── */

const TableRow = memo(function TableRow({ shipment, isLast }) {
    const customer = shipment.order?.user;
    return (
        <Box
            as="tr"
            borderBottom={isLast ? 'none' : '1px solid'}
            borderColor={BORDER}
            _hover={{ bg: SURFACE_ALT }}
            transition="background 0.15s ease"
        >
            <Box as="td" p="10px 14px" whiteSpace="nowrap">
                <Text
                    as="code"
                    bg={SURFACE_ALT}
                    color={NAVY}
                    fontSize="11px"
                    fontFamily="monospace"
                    px="6px"
                    py="2px"
                    borderRadius="4px"
                    border="1px solid"
                    borderColor={BORDER}
                >
                    {shipment.shipment_id || '—'}
                </Text>
            </Box>
            <Box as="td" p="10px 14px" whiteSpace="nowrap" fontSize="12px" color={INK}>
                {shipment.order?.order_ref || shipment.order_id || '—'}
            </Box>
            <Box as="td" p="10px 14px" whiteSpace="nowrap">
                {customer ? (
                    <Box>
                        <Text fontSize="12px" fontWeight="600" color={INK}>
                            {customer.first_name} {customer.last_name}
                        </Text>
                        <Text fontSize="10px" color={FAINT} noOfLines={1}>
                            {customer.email}
                        </Text>
                    </Box>
                ) : (
                    <Text fontSize="12px" color={FAINT}>—</Text>
                )}
            </Box>
            <Box as="td" p="10px 14px" whiteSpace="nowrap">
                <StatusBadge status={shipment.status} />
            </Box>
            <Box as="td" p="10px 14px" whiteSpace="nowrap">
                {shipment.tracking_url ? (
                    <ChakraLink
                        href={shipment.tracking_url}
                        isExternal
                        color={NAVY}
                        fontSize="12px"
                        fontWeight="700"
                        display="inline-flex"
                        alignItems="center"
                        gap="4px"
                        _hover={{ color: NAVY_DEEP, textDecoration: 'underline' }}
                    >
                        {shipment.tracking_number || 'Track package'}
                        <ExternalIcon />
                    </ChakraLink>
                ) : (
                    <Text fontSize="12px" color={FAINT}>
                        {shipment.tracking_number || 'No tracking'}
                    </Text>
                )}
            </Box>
            <Box as="td" p="10px 14px" whiteSpace="nowrap" fontSize="11px" color={MUTED}>
                {formatDate(shipment.createdAt)}
            </Box>
            <Box as="td" p="10px 14px" whiteSpace="nowrap" fontSize="11px" color={MUTED}>
                {formatDate(shipment.updatedAt)}
            </Box>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   MOBILE CARD (replaces table on small screens)
   ───────────────────────────────────────────────────────────── */

const ShipmentCard = memo(function ShipmentCard({ shipment }) {
    const customer = shipment.order?.user;
    return (
        <Box
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            p="14px"
            boxShadow={SHADOW_SM}
        >
            {/* Header: shipment ID + status */}
            <Flex justify="space-between" align="flex-start" mb="10px" gap="8px">
                <Box flex="1" minW="0">
                    <Text
                        fontSize="9px"
                        fontWeight="700"
                        color={FAINT}
                        letterSpacing="0.08em"
                        mb="2px"
                    >
                        SHIPMENT
                    </Text>
                    <Text
                        as="code"
                        fontFamily="monospace"
                        fontSize="11px"
                        fontWeight="700"
                        color={NAVY}
                        noOfLines={1}
                    >
                        {shipment.shipment_id || '—'}
                    </Text>
                </Box>
                <StatusBadge status={shipment.status} />
            </Flex>

            {/* Customer */}
            {customer && (
                <Box mb="10px">
                    <Text fontSize="13px" fontWeight="700" color={INK} noOfLines={1}>
                        {customer.first_name} {customer.last_name}
                    </Text>
                    <Text fontSize="11px" color={MUTED} noOfLines={1}>
                        {customer.email}
                    </Text>
                </Box>
            )}

            {/* Order ref + tracking */}
            <Flex justify="space-between" align="center" mb="10px" gap="8px" flexWrap="wrap">
                <Box>
                    <Text fontSize="9px" fontWeight="700" color={FAINT} letterSpacing="0.08em">
                        ORDER
                    </Text>
                    <Text fontSize="12px" fontWeight="600" color={INK}>
                        {shipment.order?.order_ref || shipment.order_id || '—'}
                    </Text>
                </Box>
                {shipment.tracking_url ? (
                    <ChakraLink
                        href={shipment.tracking_url}
                        isExternal
                        color={NAVY}
                        fontSize="12px"
                        fontWeight="700"
                        display="inline-flex"
                        alignItems="center"
                        gap="4px"
                        _hover={{ color: NAVY_DEEP, textDecoration: 'underline' }}
                    >
                        Track
                        <ExternalIcon />
                    </ChakraLink>
                ) : (
                    <Text fontSize="11px" color={FAINT}>No tracking</Text>
                )}
            </Flex>

            {/* Dates */}
            <Flex
                justify="space-between"
                pt="10px"
                borderTop="1px solid"
                borderColor={BORDER}
                fontSize="10px"
                color={FAINT}
            >
                <Box>
                    <Text fontWeight="700">Created</Text>
                    <Text>{formatDate(shipment.createdAt)}</Text>
                </Box>
                <Box textAlign="right">
                    <Text fontWeight="700">Updated</Text>
                    <Text>{formatDate(shipment.updatedAt)}</Text>
                </Box>
            </Flex>
        </Box>
    );
});

/* ─────────────────────────────────────────────────────────────
   SKELETONS
   ───────────────────────────────────────────────────────────── */

const TableSkeleton = ({ count = 5 }) => (
    <Box bg={SURFACE} border="1px solid" borderColor={BORDER} borderRadius="12px" overflow="hidden" boxShadow={SHADOW_SM}>
        {Array.from({ length: count }).map((_, i) => (
            <Flex key={i} p="14px" gap="14px" borderBottom={i === count - 1 ? 'none' : '1px solid'} borderColor={BORDER}>
                <Skeleton w="100px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="80px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton flex="1" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="90px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton w="120px" h="18px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        ))}
    </Box>
);

const CardSkeleton = ({ count = 4 }) => (
    <Flex direction="column" gap="10px">
        {Array.from({ length: count }).map((_, i) => (
            <Skeleton key={i} h="140px" borderRadius="12px" startColor={SURFACE_ALT} endColor={BORDER} />
        ))}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   PAGINATION
   ───────────────────────────────────────────────────────────── */

const Pagination = memo(function Pagination({ pagination, onPageChange }) {
    const { current_page, total_pages, total_items, items_per_page, has_next_page, has_prev_page } = pagination;
    const from = total_items === 0 ? 0 : (current_page - 1) * items_per_page + 1;
    const to   = Math.min(current_page * items_per_page, total_items);

    return (
        <Flex
            justify="space-between"
            align="center"
            wrap="wrap"
            gap="10px"
            p="14px 16px"
            mt="16px"
            bg={SURFACE}
            border="1px solid"
            borderColor={BORDER}
            borderRadius="12px"
            boxShadow={SHADOW_SM}
        >
            <Text fontSize="12px" color={MUTED}>
                Showing <Text as="span" fontWeight="700" color={INK}>{from.toLocaleString('en-NG')}</Text>
                {' '}–{' '}
                <Text as="span" fontWeight="700" color={INK}>{to.toLocaleString('en-NG')}</Text>
                {' '}of{' '}
                <Text as="span" fontWeight="700" color={INK}>{total_items.toLocaleString('en-NG')}</Text>
            </Text>

            <Flex gap="6px" align="center">
                <IconButton
                    aria-label="Previous page"
                    icon={<ChevronLeftIcon />}
                    onClick={() => onPageChange(current_page - 1)}
                    isDisabled={!has_prev_page}
                    size="sm"
                    h="32px"
                    w="32px"
                    minW="32px"
                    variant="outline"
                    borderColor={BORDER}
                    color={INK}
                    _hover={{ bg: SURFACE_ALT, borderColor: NAVY }}
                    _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                />
                <Text fontSize="12px" color={INK} fontWeight="700" px="8px">
                    Page {current_page} of {Math.max(total_pages, 1)}
                </Text>
                <IconButton
                    aria-label="Next page"
                    icon={<ChevronRightIcon />}
                    onClick={() => onPageChange(current_page + 1)}
                    isDisabled={!has_next_page}
                    size="sm"
                    h="32px"
                    w="32px"
                    minW="32px"
                    variant="outline"
                    borderColor={BORDER}
                    color={INK}
                    _hover={{ bg: SURFACE_ALT, borderColor: NAVY }}
                    _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                />
            </Flex>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────────────────────── */

const EmptyState = ({ onRetry, hasFilters }) => (
    <Flex
        direction="column"
        align="center"
        justify="center"
        py="60px"
        px="20px"
        textAlign="center"
        gap="12px"
        bg={SURFACE}
        border="1px solid"
        borderColor={BORDER}
        borderRadius="12px"
        boxShadow={SHADOW_SM}
    >
        <Flex w="60px" h="60px" borderRadius="full" bg={SURFACE_ALT} color={FAINT} align="center" justify="center">
            <InboxIcon />
        </Flex>
        <Box>
            <Heading as="h3" fontSize="15px" fontWeight="800" color={INK} fontFamily={FONT} mb="4px">
                {hasFilters ? 'No shipments match your filters' : 'No shipments yet'}
            </Heading>
            <Text fontSize="12px" color={MUTED}>
                {hasFilters
                    ? 'Try adjusting your filters or date range.'
                    : 'Shipments will appear here once orders are placed.'}
            </Text>
        </Box>
        <Button
            onClick={onRetry}
            size="sm"
            bg={NAVY}
            color="white"
            _hover={{ bg: NAVY_DEEP }}
            fontSize="12px"
            fontWeight="700"
            borderRadius="8px"
            mt="4px"
            leftIcon={<RefreshIcon />}
        >
            Try again
        </Button>
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

const INITIAL_FILTERS = {
    status: '',
    order_id: '',
    shipment_id: '',
    vendor_id: '',
    user_id: '',
    start_date: '',
    end_date: '',
};

const INITIAL_PAGINATION = {
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: ITEMS_PER_PAGE,
    has_next_page: false,
    has_prev_page: false,
};

const Shipments = () => {
    /* ── State ──────────────────────────────────────────────── */
    const [shipments, setShipments]   = useState([]);
    const [stats, setStats]           = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [pagination, setPagination] = useState(INITIAL_PAGINATION);
    const [filters, setFilters]       = useState(INITIAL_FILTERS);
    const [page, setPage]             = useState(1);
    const [socketConnected, setSocketConnected] = useState(false);

    /* Debounce free-text filter inputs (text + dates) so we don't spam the API */
    const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);

    /* Ref to always read the latest filters from inside the socket callback */
    const filtersRef = useRef(filters);
    useEffect(() => { filtersRef.current = filters; }, [filters]);

    /* ── Active filters ─────────────────────────────────────── */
    const hasActiveFilters = useMemo(
        () => Object.values(filters).some((v) => v !== '' && v != null),
        [filters]
    );

    /* ── Data fetching ──────────────────────────────────────── */

    const fetchShipments = useCallback(async (pageNum, currentFilters) => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                page:  String(pageNum),
                limit: String(ITEMS_PER_PAGE),
                ...cleanFilters(currentFilters),
            });

            dlog('[Shipments] fetching', params.toString());

            const response = await apiService.get(`/shipment?${params}`);

            if (response.success) {
                setShipments(response.data || []);
                setPagination(response.pagination || INITIAL_PAGINATION);
            } else {
                setError(response.message || 'Failed to fetch shipments');
            }
        } catch (err) {
            dlog('[Shipments] fetch error', err);
            setError(err?.message || err?.error || 'Failed to load shipments');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            dlog('[Shipments] fetching stats');
            const response = await apiService.get('/shipment/stats');
            if (response.success) {
                setStats(response.data);
            }
        } catch (err) {
            dlog('[Shipments] stats error', err);
        }
    }, []);

    /* Refetch when page or debounced filters change */
    useEffect(() => {
        fetchShipments(page, debouncedFilters);
    }, [page, debouncedFilters, fetchShipments]);

    /* Reset to page 1 when filters change (but not on initial mount) */
    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setPage(1);
    }, [debouncedFilters]);

    /* Fetch stats on mount only */
    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    /* ── Socket.IO — inside useEffect, properly cleaned up ── */
    useEffect(() => {
        const socket = io(SOCKET_URL, {
            transports: ['websocket', 'polling'],
            withCredentials: true,
        });

        const handleConnect    = () => { dlog('[Socket] connected');    setSocketConnected(true); };
        const handleDisconnect = () => { dlog('[Socket] disconnected'); setSocketConnected(false); };
        const handleError      = (err) => dlog('[Socket] error', err);

        const handleShipmentUpdate = (update) => {
            dlog('[Socket] shipment_update', update);

            setShipments((prev) => {
                const idx = prev.findIndex(
                    (s) => s.shipment_id === update.shipment_id || s.id === update.id
                );
                if (idx !== -1) {
                    const next = [...prev];
                    next[idx] = { ...next[idx], ...update };
                    return next;
                }
                return [update, ...prev];
            });

            /* Refresh stats if status changed — fresh fetch, no stale closure */
            if (update.status) {
                fetchStats();
            }
        };

        socket.on('connect',         handleConnect);
        socket.on('disconnect',      handleDisconnect);
        socket.on('error',           handleError);
        socket.on('shipment_update', handleShipmentUpdate);

        socket.emit('join_shipment', 'all_shipments');

        return () => {
            socket.off('connect',         handleConnect);
            socket.off('disconnect',      handleDisconnect);
            socket.off('error',           handleError);
            socket.off('shipment_update', handleShipmentUpdate);
            socket.disconnect();
            dlog('[Socket] cleaned up on unmount');
        };
    }, [fetchStats]);

    /* ── Handlers ───────────────────────────────────────────── */

    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    }, []);

    const handlePageChange = useCallback(
        (newPage) => {
            if (newPage < 1 || newPage > pagination.total_pages) return;
            setPage(newPage);
        },
        [pagination.total_pages]
    );

    const handleRefresh = useCallback(() => {
        fetchShipments(page, filters);
        fetchStats();
    }, [fetchShipments, fetchStats, page, filters]);

    /* ── Derived stats data for cards ───────────────────────── */
    const statCards = useMemo(() => {
        const cards = [
            { label: 'Total Shipments', value: pagination.total_items, accent: NAVY },
        ];

        if (stats?.status_breakdown?.length) {
            stats.status_breakdown.forEach((s) => {
                const meta = getStatusMeta(s.status);
                cards.push({
                    label: meta.label,
                    value: s.count,
                    accent: meta.color,
                });
            });
        }

        return cards;
    }, [stats, pagination.total_items]);

    /* ── Render ─────────────────────────────────────────────── */

    return (
        <Box
            p={{ base: '16px', md: '24px' }}
            bg={SURFACE_ALT}
            minH="100vh"
            fontFamily={FONT}
        >
            <Box maxW="1400px" mx="auto">
                {/* ── HEADER ── */}
                <Flex
                    justify="space-between"
                    align={{ base: 'flex-start', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap="10px"
                    mb="20px"
                >
                    <Box>
                        <Heading
                            as="h1"
                            fontSize={{ base: '22px', md: '26px' }}
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.02em"
                            lineHeight="1.1"
                            mb="4px"
                        >
                            Shipments
                        </Heading>
                        <ConnectionDot connected={socketConnected} />
                    </Box>

                    <Button
                        onClick={handleRefresh}
                        isLoading={loading}
                        bg={NAVY}
                        color="white"
                        _hover={{ bg: NAVY_DEEP }}
                        _active={{ bg: NAVY_DEEP }}
                        size="sm"
                        h="38px"
                        px="16px"
                        fontSize="12.5px"
                        fontWeight="700"
                        fontFamily={FONT}
                        borderRadius="8px"
                        leftIcon={<RefreshIcon />}
                    >
                        Refresh
                    </Button>
                </Flex>

                {/* ── ERROR ── */}
                {error && (
                    <Flex
                        bg={DANGER_BG}
                        border="1px solid"
                        borderColor={`${DANGER}40`}
                        color={DANGER}
                        p="12px 16px"
                        borderRadius="10px"
                        mb="16px"
                        align="center"
                        justify="space-between"
                        gap="10px"
                    >
                        <Text fontSize="12px" fontWeight="700">
                            {error}
                        </Text>
                        <IconButton
                            aria-label="Dismiss error"
                            icon={<CloseIcon />}
                            onClick={() => setError(null)}
                            size="xs"
                            variant="ghost"
                            color={DANGER}
                            _hover={{ bg: 'whiteAlpha.500' }}
                        />
                    </Flex>
                )}

                {/* ── STATS ── */}
                {statCards.length > 0 && (
                    <SimpleGrid
                        columns={{ base: 2, sm: 3, md: 4, lg: 6 }}
                        spacing="10px"
                        mb="20px"
                    >
                        {statCards.map((s, i) => (
                            <StatCard key={`${s.label}-${i}`} label={s.label} value={s.value} accent={s.accent} />
                        ))}
                    </SimpleGrid>
                )}

                {/* ── FILTERS ── */}
                <FilterBar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearAll={handleClearFilters}
                    hasActiveFilters={hasActiveFilters}
                />

                {/* ── BODY ── */}
                {loading && shipments.length === 0 ? (
                    <>
                        <Box display={{ base: 'none', md: 'block' }}>
                            <TableSkeleton count={6} />
                        </Box>
                        <Box display={{ base: 'block', md: 'none' }}>
                            <CardSkeleton count={4} />
                        </Box>
                    </>
                ) : shipments.length === 0 ? (
                    <EmptyState
                        onRetry={() => fetchShipments(1, filters)}
                        hasFilters={hasActiveFilters}
                    />
                ) : (
                    <>
                        {/* DESKTOP — table */}
                        <Box
                            display={{ base: 'none', md: 'block' }}
                            bg={SURFACE}
                            border="1px solid"
                            borderColor={BORDER}
                            borderRadius="12px"
                            overflow="hidden"
                            boxShadow={SHADOW_SM}
                            opacity={loading ? 0.6 : 1}
                            transition="opacity 0.2s ease"
                        >
                            <Box overflowX="auto">
                                <Box as="table" w="full" borderCollapse="collapse" minW="900px">
                                    <Box as="thead" bg={SURFACE_ALT}>
                                        <Box as="tr">
                                            {[
                                                'Shipment ID',
                                                'Order Ref',
                                                'Customer',
                                                'Status',
                                                'Tracking',
                                                'Created',
                                                'Updated',
                                            ].map((header) => (
                                                <Box
                                                    key={header}
                                                    as="th"
                                                    p="12px 14px"
                                                    textAlign="left"
                                                    fontSize="10px"
                                                    fontWeight="800"
                                                    color={MUTED}
                                                    letterSpacing="0.08em"
                                                    textTransform="uppercase"
                                                    whiteSpace="nowrap"
                                                    borderBottom="1px solid"
                                                    borderColor={BORDER}
                                                >
                                                    {header}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box as="tbody">
                                        {shipments.map((s, i) => (
                                            <TableRow
                                                key={s.id ?? s.shipment_id ?? i}
                                                shipment={s}
                                                isLast={i === shipments.length - 1}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* MOBILE — cards */}
                        <Flex
                            display={{ base: 'flex', md: 'none' }}
                            direction="column"
                            gap="10px"
                            opacity={loading ? 0.6 : 1}
                            transition="opacity 0.2s ease"
                        >
                            {shipments.map((s, i) => (
                                <ShipmentCard
                                    key={s.id ?? s.shipment_id ?? i}
                                    shipment={s}
                                />
                            ))}
                        </Flex>

                        <Pagination pagination={pagination} onPageChange={handlePageChange} />
                    </>
                )}
            </Box>
        </Box>
    );
};

export default Shipments;